import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ReconciliationLine, ReconciliationSession, UserReplaceProfile } from '../types'
import { OverallStatusBadge } from './StatusBadge'
import { ValidationSummary, type XmlTarget } from './ValidationSummary'
import { DocumentsPanel } from './DocumentsPanel'
import { LineTable } from './LineTable'
import { LineDetailPanel } from './LineDetailPanel'
import { XmlViewer } from './XmlViewer'
import { EmailConfirmModal } from './EmailConfirmModal'
import { FindingsEmailModal } from './FindingsEmailModal'
import { ResizableSplitPanel } from './ResizableSplitPanel'
import { downloadSessionZip, getActiveReplaceProfile, deactivateAllReplaceProfiles } from '../api/client'
import type { SendEmailResult } from '../api/client'
import { useStore } from '../store'
import { buildEmailFiles } from '../utils/emailFilename'

function ActiveProfileBanner({ profile, onDeactivate }: { profile: UserReplaceProfile; onDeactivate: () => void }) {
  const { t } = useTranslation()

  const fields: string[] = [
    profile.project_number ? `${t('reconciliation_table.profile_project')}: ${profile.project_number}` : '',
    profile.sender_gln    ? `${t('reconciliation_table.profile_gln_sender')}: ${profile.sender_gln}` : '',
    profile.sender_name   ? `${t('reconciliation_table.profile_name_sender')}: ${profile.sender_name}` : '',
    profile.receiver_gln  ? `${t('reconciliation_table.profile_gln_receiver')}: ${profile.receiver_gln}` : '',
    profile.receiver_name ? `${t('reconciliation_table.profile_name_receiver')}: ${profile.receiver_name}` : '',
  ].filter(Boolean)

  return (
    <div className="rounded-xl border border-amber-300 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 flex items-start justify-between gap-3">
      <div className="flex items-start gap-3 min-w-0">
        <span className="text-amber-500 text-lg leading-none mt-0.5">⚠</span>
        <div className="min-w-0">
          <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 uppercase tracking-wide mb-1">
            {t('reconciliation_table.profile_banner_title')}
          </p>
          <p className="text-sm font-medium text-amber-900">
            {profile.name}
          </p>
          {fields.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
              {fields.map(f => (
                <span key={f} className="text-xs font-mono text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/30 rounded px-1.5 py-0.5">
                  {f}
                </span>
              ))}
            </div>
          )}
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5">
            {t('reconciliation_table.profile_banner_description')}
          </p>
        </div>
      </div>
      <button
        onClick={onDeactivate}
        className="shrink-0 text-xs font-medium text-amber-700 dark:text-amber-300 hover:text-red-700 border border-amber-300 dark:border-amber-800/50 hover:border-red-300 bg-white dark:bg-gray-800 hover:bg-red-50 rounded-lg px-3 py-1.5 transition-colors"
        title={t('reconciliation_table.profile_deactivate_title')}
      >
        {t('reconciliation_table.profile_deactivate')}
      </button>
    </div>
  )
}

interface Props {
  session: ReconciliationSession
}

export function ReconciliationTable({ session }: Props) {
  const { t } = useTranslation()
  type XmlLayout = 'below' | 'right'

  const { activeProfile, setActiveProfile, currentUser, loadedTestCase } = useStore()
  const [showModal, setShowModal] = useState(false)
  const [showFindingsModal, setShowFindingsModal] = useState(false)

  const [emailSuccess, setEmailSuccess] = useState<string | null>(null)
  const [selectedLine, setSelectedLine] = useState<ReconciliationLine | null>(null)
  const [headerTarget, setHeaderTarget] = useState<XmlTarget | null>(null)
  const [xmlLayout, setXmlLayout] = useState<XmlLayout>(() =>
    (localStorage.getItem('reconciliation-xml-layout') as XmlLayout | null) ?? 'right'
  )

  const updateXmlLayout = (layout: XmlLayout) => {
    setXmlLayout(layout)
    localStorage.setItem('reconciliation-xml-layout', layout)
  }

  useEffect(() => {
    getActiveReplaceProfile()
      .then(p => setActiveProfile(p))
      .catch(() => {/* stil falen — banner is optioneel */})
  }, [])

  const modalFiles = buildEmailFiles(session, {
    ordrsp: session.ordrsp_filename !== null,
    desadv: session.desadv_filename !== null,
    invoice: session.invoice_filename !== null,
  })

  const handleSent = (result: SendEmailResult) => {
    setShowModal(false)
    const refs = [result.or_ref, result.da_ref, result.inv_ref].filter(Boolean).join(' / ')
    setEmailSuccess(t('reconciliation_table.email_sent_success', { count: result.sent, to: result.to, refs }))
    setTimeout(() => setEmailSuccess(null), 4000)
  }

  const sessionHasPdf = session.ordrsp_has_pdf || session.desadv_has_pdf || session.invoice_has_pdf

  return (
    <div className="space-y-4">
      {showModal && (
        <EmailConfirmModal
          sessionId={session.id}
          files={modalFiles}
          hasPdf={sessionHasPdf}
          onSent={handleSent}
          onCancel={() => setShowModal(false)}
        />
      )}
      {showFindingsModal && (
        <FindingsEmailModal
          session={session}
          currentUserEmail={currentUser?.email}
          onSent={to => {
            setShowFindingsModal(false)
            setEmailSuccess(t('reconciliation_table.findings_email_sent', { to }))
            setTimeout(() => setEmailSuccess(null), 4000)
          }}
          onCancel={() => setShowFindingsModal(false)}
        />
      )}
      {/* Testscenario banner */}
      {loadedTestCase && (
        <div className="rounded-xl border border-blue-200 dark:border-blue-800/50 bg-blue-50 dark:bg-blue-900/20 px-4 py-2.5 flex items-center gap-3">
          <span className="text-blue-400 text-base leading-none">⬡</span>
          <div className="flex items-center gap-2 flex-wrap text-sm">
            <span className="text-xs text-blue-500 dark:text-blue-400 uppercase tracking-wide font-semibold shrink-0">{t('reconciliation_table.test_scenario_label')}</span>
            <span className="text-blue-900 dark:text-blue-200 font-medium">{loadedTestCase.scenarioName}</span>
            <span className="text-blue-300 dark:text-blue-600">›</span>
            <span className="text-blue-700 dark:text-blue-300">{loadedTestCase.testCaseName}</span>
          </div>
        </div>
      )}

      {/* Sessie header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="space-y-0.5">
            <div className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide">{t('reconciliation_table.header_comparison')}</div>
            <div className="font-semibold text-gray-500 dark:text-gray-400 font-mono text-sm">#{session.id}</div>
          </div>
          <div className="space-y-0.5">
            <div className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide">{t('reconciliation_table.header_project')}</div>
            <div className="font-semibold text-gray-900 dark:text-gray-100">{session.project_number ?? '—'}</div>
          </div>
          <div className="space-y-0.5">
            <div className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide">{t('reconciliation_table.header_supplier')}</div>
            <div className="font-semibold text-gray-900 dark:text-gray-100">{session.supplier_name ?? '—'}</div>
          </div>
          <div className="space-y-0.5">
            <div className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide">{t('reconciliation_table.header_invoice')}</div>
            <div className="font-semibold text-gray-900 dark:text-gray-100">{session.invoice_number ?? '—'}</div>
          </div>
          <div className="space-y-0.5">
            <div className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide">{t('reconciliation_table.header_order_response')}</div>
            <div className="font-semibold text-gray-900 dark:text-gray-100">{session.order_response_number ?? '—'}</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <OverallStatusBadge status={session.overall_status} />
            <button
              onClick={() => downloadSessionZip(session.id)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 transition-colors"
              title={t('reconciliation_table.download_xml_title')}
            >
              ↓ {t('reconciliation_table.download_xml')}
            </button>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setShowModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-indigo-200 dark:border-indigo-800/50 bg-indigo-50 dark:bg-indigo-900/20 text-xs font-medium text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
                title={t('reconciliation_table.send_email_title')}
              >
                ✉ {t('reconciliation_table.send_email')}
              </button>
              <button
                onClick={() => setShowFindingsModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-teal-200 dark:border-teal-800/50 bg-teal-50 dark:bg-teal-900/20 text-xs font-medium text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-900/30 transition-colors"
                title={t('reconciliation_table.findings_email_title')}
              >
                ✉ {t('reconciliation_table.findings_email')}
              </button>
              {emailSuccess && (
                <span className="text-xs text-green-600 font-medium">✓ {emailSuccess}</span>
              )}
            </div>

            {/* XML-layout toggle */}
            <div className="flex items-center rounded-full border border-gray-200 dark:border-gray-700 overflow-hidden text-xs">
              <button
                onClick={() => updateXmlLayout('below')}
                title={t('reconciliation_table.xml_layout_below_title')}
                className={`flex items-center gap-1 px-2.5 py-1 transition-colors ${
                  xmlLayout === 'below'
                    ? 'bg-gray-700 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3">
                  <rect x="1" y="2" width="14" height="5" rx="1"/>
                  <rect x="1" y="9" width="14" height="5" rx="1"/>
                </svg>
                {t('reconciliation_table.xml_layout_below')}
              </button>
              <button
                onClick={() => updateXmlLayout('right')}
                title={t('reconciliation_table.xml_layout_right_title')}
                className={`flex items-center gap-1 px-2.5 py-1 transition-colors border-l border-gray-200 dark:border-gray-700 ${
                  xmlLayout === 'right'
                    ? 'bg-gray-700 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3">
                  <rect x="1" y="2" width="6" height="12" rx="1"/>
                  <rect x="9" y="2" width="6" height="12" rx="1"/>
                </svg>
                {t('reconciliation_table.xml_layout_right')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {activeProfile && (
        <ActiveProfileBanner
          profile={activeProfile}
          onDeactivate={() => {
            deactivateAllReplaceProfiles()
              .then(() => setActiveProfile(null))
              .catch(() => {/* stil falen */})
          }}
        />
      )}
      <ValidationSummary
        session={session}
        onInfoClick={target => {
          setSelectedLine(null)
          setHeaderTarget(target)
        }}
      />
      {/* Layout: gestapeld (Onder) of zij-aan-zij (Naast) afhankelijk van voorkeur */}
      {(() => {
        const lineHasXml = !!(
          selectedLine?.ordrsp_xpath ||
          selectedLine?.desadv_xpath ||
          selectedLine?.invoice_xpath
        )
        const showXmlViewer = (selectedLine && lineHasXml) || !!headerTarget

        // XmlViewer voor 'Naast'-modus (sidePanel = grotere hoogte)
        const xmlViewerSide = selectedLine && lineHasXml ? (
          <XmlViewer
            session={session}
            selectedLine={selectedLine}
            onClose={() => setSelectedLine(null)}
            sidePanel
          />
        ) : headerTarget ? (
          <XmlViewer
            session={session}
            headerXpaths={headerTarget.xpaths}
            headerLabel={headerTarget.label}
            onClose={() => setHeaderTarget(null)}
            sidePanel
          />
        ) : null

        // XmlViewer voor 'Onder'-modus (standaard hoogte)
        const xmlViewerBelow = selectedLine && lineHasXml ? (
          <XmlViewer
            session={session}
            selectedLine={selectedLine}
            onClose={() => setSelectedLine(null)}
          />
        ) : headerTarget ? (
          <XmlViewer
            session={session}
            headerXpaths={headerTarget.xpaths}
            headerLabel={headerTarget.label}
            onClose={() => setHeaderTarget(null)}
          />
        ) : null

        const leftContent = (
          <div className="space-y-4">
            <DocumentsPanel
              session={session}
              onInfoClick={target => {
                setSelectedLine(null)
                setHeaderTarget(target)
              }}
            />
            <LineTable
              session={session}
              onLineClick={line => {
                setHeaderTarget(null)
                setSelectedLine(prev => prev?.id === line.id ? null : line)
              }}
              selectedLineId={selectedLine?.id}
            />
            {selectedLine && (
              <LineDetailPanel
                line={selectedLine}
                session={session}
                onClose={() => setSelectedLine(null)}
              />
            )}
          </div>
        )

        if (showXmlViewer && xmlLayout === 'right') {
          return (
            <ResizableSplitPanel
              defaultLeftPercent={50}
              left={leftContent}
              right={xmlViewerSide!}
            />
          )
        }

        return (
          <>
            {leftContent}
            {showXmlViewer && xmlViewerBelow}
          </>
        )
      })()}
    </div>
  )
}
